import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import Loading from '../../../components/common/Loading/Loading';
import { Transaction, TransStatus, TransType, User } from '../../../Entities';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './Transactions.module.css';
import { useAuth } from '../../../contexts/AuthContext';

const formatDate = (timestamp?: number) =>
    typeof timestamp === 'number' ? new Date(timestamp * 1000).toLocaleString('cs-CZ') : 'Neuvedeno';

const getStatusName = (statusId?: number) => {
    return TransStatus.get({ id: statusId })?.name;
};

const getTypeName = (typeId?: number) => {
    return TransType.get({ id: typeId })?.name;
};

// Default status filter shown on first load: 2 = pending / waiting for processing.
const ALL_FILTER_VALUE = '';
const PAGE_SIZE = 20;

const Transactions = () => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savingId, setSavingId] = useState<number | null>(null);
    const [processingPayoutId, setProcessingPayoutId] = useState<number | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState({ amount: '', bank_account: '', trans_status_id: '', trans_type_id: '' });
    const [error, setError] = useState(false);
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>(String(ALL_FILTER_VALUE));
    const [typeFilter, setTypeFilter] = useState<string>(ALL_FILTER_VALUE);
    const { setUser } = useAuth();

    const loadTransactions = useCallback(async (targetPage: number, typeValue: string, statusValue: string, isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError(false);

        try {
            const transTypeId = typeValue === ALL_FILTER_VALUE ? -1 : Number(typeValue);
            const transStatusId = statusValue === ALL_FILTER_VALUE ? -1 : Number(statusValue);
            const data = await Transaction.getAll(transTypeId, transStatusId, targetPage, PAGE_SIZE);
            setTransactions(data);
            setHasNextPage(data.length === PAGE_SIZE);
            setPage(targetPage);
        } catch (requestError) {
            console.error(requestError);
            setError(true);
            notify.error('Nepodařilo se načíst transakce');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        loadTransactions(1, typeFilter, statusFilter);
    }, [loadTransactions]);

    const startEditing = (transaction: Transaction) => {
        if (transaction.id === undefined) return;
        setEditingId(transaction.id);
        setEditForm({
            amount: transaction.amount === undefined ? '' : String(transaction.amount),
            bank_account: transaction.bank_account || '',
            trans_status_id: String(transaction.trans_status_id),
            trans_type_id: String(transaction.trans_type_id),
        });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditForm({ amount: '', bank_account: '', trans_status_id: '', trans_type_id: '' });
    };

    const goToPage = (targetPage: number) => {
        if (targetPage < 1 || loading || refreshing) return;
        cancelEditing();
        loadTransactions(targetPage, typeFilter, statusFilter);
    };

    const handleStatusFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setStatusFilter(value);
        cancelEditing();
        loadTransactions(1, typeFilter, value);
    };

    const handleTypeFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setTypeFilter(value);
        cancelEditing();
        loadTransactions(1, value, statusFilter);
    };

    const saveTransaction = async (transaction: Transaction) => {
        if (transaction.id === undefined) return;

        const amount = Number(editForm.amount);
        if (!editForm.amount.trim() || !Number.isFinite(amount) || amount < 0) {
            notify.error('Zadejte platnou částku');
            return;
        }
        const statusId = Number(editForm.trans_status_id);
        if (!editForm.trans_status_id.trim() || !Number.isInteger(statusId)) {
            notify.error('Vyberte platný stav transakce');
            return;
        }
        const typeId = Number(editForm.trans_type_id);
        if (!editForm.trans_type_id.trim() || !Number.isInteger(typeId)) {
            notify.error('Vyberte platný typ transakce');
            return;
        }

        transaction.amount = amount;
        transaction.bank_account = editForm.bank_account;
        transaction.trans_status_id = Number(editForm.trans_status_id);
        transaction.trans_type_id = Number(editForm.trans_type_id);
        setSavingId(transaction.id);

        try {
            await transaction.update();
            setUser(await User.get());
            cancelEditing();
            notify.success('Transakce byla aktualizována');
        } catch (requestError) {
            console.error(requestError);
            notify.error('Transakci se nepodařilo aktualizovat');
        } finally {
            setSavingId(null);
        }
    };

    const handlePayOut = async (transaction: Transaction) => {
        if (transaction.id === undefined) return;

        setProcessingPayoutId(transaction.id);
        try {
            await transaction.payOut();
            setUser(await User.get());
            notify.success('Výplata byla úspěšně provedena');
            loadTransactions(page, typeFilter, statusFilter, true);
        } catch (requestError) {
            console.error(requestError);
            notify.error('Výplatu se nepodařilo provést');
        } finally {
            setProcessingPayoutId(null);
        }
    };

    const statusOptions = TransStatus.values;
    const typeOptions = TransType.values;

    if (loading) return <Loading />;

    return (
        <div className={styles.transactions}>
            <div className={styles.header}>
                <div>
                    <p className={styles.eyebrow}>Administrace</p>
                    <h1>Transakce</h1>
                    <p className={styles.description}>Přehled žádostí o výplatu podle zvoleného typu a stavu.</p>
                </div>
                <Button variant="secondary" onClick={() => loadTransactions(page, typeFilter, statusFilter, true)} disabled={refreshing}>
                    {refreshing ? 'Načítání...' : '↻ Obnovit'}
                </Button>
            </div>

            <Card className={styles.tableCard} padding="none" hover={false}>
                <div className={styles.filters}>
                    <label className={styles.filterLabel} htmlFor="trans-type-filter">
                        Typ transakce
                        <select
                            id="trans-type-filter"
                            className="form-input"
                            value={typeFilter}
                            onChange={handleTypeFilterChange}
                            disabled={loading || refreshing}
                        >
                            <option value={ALL_FILTER_VALUE}>Všechny typy</option>
                            {TransType.values.map((type) => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </select>
                    </label>

                    <label className={styles.filterLabel} htmlFor="trans-status-filter">
                        Stav transakce
                        <select
                            id="trans-status-filter"
                            className="form-input"
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            disabled={loading || refreshing}
                        >
                            <option value={ALL_FILTER_VALUE}>Všechny stavy</option>
                            {TransStatus.values.map((status) => (
                                <option key={status.id} value={status.id}>{status.name}</option>
                            ))}
                        </select>
                    </label>
                </div>

                <div className={styles.summary}>
                    <span>Celkem nalezeno</span>
                    <strong>{transactions.length}</strong>
                </div>

                {error ? (
                    <div className={styles.message}>
                        <strong>Transakce se nepodařilo načíst.</strong>
                        <Button variant="secondary" size="small" onClick={() => loadTransactions(page, typeFilter, statusFilter)}>
                            Zkusit znovu
                        </Button>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className={styles.message}>Žádné čekající transakce.</div>
                ) : (
                    <>
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Uživatel</th>
                                        <th>Částka</th>
                                        <th>Bankovní účet</th>
                                        <th>Vytvořeno</th>
                                        <th>Typ</th>
                                        <th>Stav</th>
                                        <th>Akce</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((transaction) => (
                                        <tr key={transaction.id ?? `${transaction.user_id}-${transaction.creation_timestamp}`}>
                                            {editingId === transaction.id ? (
                                                <>
                                                    <td>#{transaction.id ?? '—'}</td>
                                                    <td>{transaction.user_name}</td>
                                                    <td><input className={`form-input ${styles.editInput}`} type="number" min="0" step="0.01" value={editForm.amount} onChange={(event) => setEditForm({ ...editForm, amount: event.target.value })} aria-label="Částka" /></td>
                                                    <td><input className={`form-input ${styles.editInput}`} value={editForm.bank_account} onChange={(event) => setEditForm({ ...editForm, bank_account: event.target.value })} aria-label="Bankovní účet" /></td>
                                                    <td>{formatDate(transaction.creation_timestamp)}</td>
                                                    <td>
                                                        <select className={`form-input ${styles.editInput}`} value={editForm.trans_type_id} onChange={(event) => setEditForm({ ...editForm, trans_type_id: event.target.value })} aria-label="Typ">
                                                            <option value="">Vyberte typ</option>
                                                            {typeOptions.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <select className={`form-input ${styles.editInput}`} value={editForm.trans_status_id} onChange={(event) => setEditForm({ ...editForm, trans_status_id: event.target.value })} aria-label="Stav">
                                                            <option value="">Vyberte stav</option>
                                                            {statusOptions.map((status) => <option key={status.id} value={status.id}>{status.name}</option>)}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <div className={styles.actions}>
                                                            <Button size="small" onClick={() => saveTransaction(transaction)} disabled={savingId === transaction.id}>{savingId === transaction.id ? 'Ukládám...' : 'Uložit'}</Button>
                                                            <Button size="small" variant="secondary" onClick={cancelEditing} disabled={savingId === transaction.id}>Zrušit</Button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>#{transaction.id ?? '—'}</td>
                                                    <td>{transaction.user_name}</td>
                                                    <td className={styles.amount}>{transaction.amount}</td>
                                                    <td className={styles.account}>{transaction.bank_account}</td>
                                                    <td>{formatDate(transaction.creation_timestamp)}</td>
                                                    <td>{getTypeName(transaction.trans_type_id)}</td>
                                                    <td><span className={styles.status}>{getStatusName(transaction.trans_status_id)}</span></td>
                                                    <td>
                                                        <div className={styles.actions}>
                                                            <Button size="small" variant="secondary" onClick={() => startEditing(transaction)} disabled={savingId !== null || processingPayoutId !== null}>Upravit</Button>
                                                            {(transaction.trans_status_id == 2) &&
                                                                <Button size="small" onClick={() => handlePayOut(transaction)} disabled={savingId !== null || processingPayoutId !== null}>
                                                                    {processingPayoutId === transaction.id ? 'Vyplácím...' : 'Vyplatit'}
                                                                </Button>
                                                            }
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.mobileList}>
                            {transactions.map((transaction) => (
                                <article className={styles.mobileItem} key={transaction.id ?? `${transaction.user_id}-${transaction.creation_timestamp}`}>
                                    <div className={styles.mobileItemHeader}>
                                        <strong>{transaction.user_name}</strong>
                                        <span className={styles.status}>{getStatusName(transaction.trans_status_id)}</span>
                                    </div>

                                    {editingId === transaction.id ? (
                                        <div className={styles.mobileEditForm}>
                                            <label>
                                                Částka
                                                <input className="form-input" type="number" min="0" step="0.01" value={editForm.amount} onChange={(event) => setEditForm({ ...editForm, amount: event.target.value })} />
                                            </label>
                                            <label>
                                                Bankovní účet
                                                <input className="form-input" value={editForm.bank_account} onChange={(event) => setEditForm({ ...editForm, bank_account: event.target.value })} />
                                            </label>
                                            <label>
                                                Typ
                                                <select className="form-input" value={editForm.trans_type_id} onChange={(event) => setEditForm({ ...editForm, trans_type_id: event.target.value })}>
                                                    <option value="">Vyberte typ</option>
                                                    {typeOptions.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                                                </select>
                                            </label>
                                            <label>
                                                Stav
                                                <select className="form-input" value={editForm.trans_status_id} onChange={(event) => setEditForm({ ...editForm, trans_status_id: event.target.value })}>
                                                    <option value="">Vyberte stav</option>
                                                    {statusOptions.map((status) => <option key={status.id} value={status.id}>{status.name}</option>)}
                                                </select>
                                            </label>
                                            <div className={styles.actions}>
                                                <Button onClick={() => saveTransaction(transaction)} disabled={savingId === transaction.id}>{savingId === transaction.id ? 'Ukládám...' : 'Uložit'}</Button>
                                                <Button variant="secondary" onClick={cancelEditing} disabled={savingId === transaction.id}>Zrušit</Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={styles.mobileAmount}>{transaction.amount}</div>

                                            <div className={styles.mobileMeta}>
                                                <div className={styles.mobileMetaItem}>
                                                    <span>Účet</span>
                                                    <strong>{transaction.bank_account || 'Neuvedeno'}</strong>
                                                </div>
                                                <div className={styles.mobileMetaItem}>
                                                    <span>Vytvořeno</span>
                                                    <strong>{formatDate(transaction.creation_timestamp)}</strong>
                                                </div>
                                                <div className={styles.mobileMetaItem}>
                                                    <span>Typ</span>
                                                    <strong>{getTypeName(transaction.trans_type_id)}</strong>
                                                </div>
                                            </div>

                                            <div className={styles.actions}>
                                                <Button variant="secondary" onClick={() => startEditing(transaction)} disabled={savingId !== null || processingPayoutId !== null}>
                                                    Upravit
                                                </Button>
                                                {(transaction.trans_status_id == 2) &&

                                                    <Button onClick={() => handlePayOut(transaction)} disabled={savingId !== null || processingPayoutId !== null}>
                                                        {processingPayoutId === transaction.id ? 'Vyplácím...' : 'Vyplatit'}
                                                    </Button>
                                                }
                                            </div>
                                        </>
                                    )}
                                </article>
                            ))}
                        </div>
                    </>
                )}

                {!error && (transactions.length > 0 || page > 1) && (
                    <div className={styles.pagination}>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={() => goToPage(page - 1)}
                            disabled={page <= 1 || loading || refreshing}
                        >
                            ← Předchozí
                        </Button>
                        <span className={styles.pageIndicator}>Strana {page}</span>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={() => goToPage(page + 1)}
                            disabled={!hasNextPage || loading || refreshing}
                        >
                            Další →
                        </Button>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Transactions;